import { useEffect, useRef, useState } from "react";
import { ChevronDown, Info } from "lucide-react";

export const ROLE_TIPS = {
  check_in_helper: "Registers players, reveals QR codes, checks them in and directs them to the playing area.",
  roster_ops_helper: "Marks players available or assigns them to sessions at an OPS desk or on the move.",
  court_ops_helper: "Manages courts, inputs coaching sessions, and performs all Check-In and OPS Assist functions.",
  deputy_helper: "Full operational control — updates player details, purchases add-ons, and performs all role functions above.",
};

const DROPDOWN_LABELS = { check_in_helper: "Check-In Helper", deputy_helper: "Deputy Admin" };
const labelFor = (k, roleMeta) => DROPDOWN_LABELS[k] || roleMeta[k]?.label;

// Role dropdown with an (i) per option: hover on desktop, tap on mobile shows the tip.
export function HelperRolePicker({ value, onChange, roles, roleMeta, testId }) {
  const [open, setOpen] = useState(false);
  const [tipKey, setTipKey] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("touchstart", onDoc); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] focus:outline-none px-3 py-2 text-sm text-left flex items-center justify-between">
        <span>{labelFor(value, roleMeta)}</span>
        <ChevronDown className={`w-4 h-4 text-[#525252] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#262626] shadow-xl" data-testid={`${testId}-menu`}>
          {roles.map((k) => {
            const showTip = tipKey === k || hoverKey === k;
            return (
              <div key={k} className={`border-b border-[#1a1a1a] last:border-b-0 ${k === value ? "bg-[#CCFF00]/5" : ""}`}>
                <div className="flex items-center">
                  <button type="button" onClick={() => { onChange(k); setOpen(false); setTipKey(null); }}
                    data-testid={`${testId}-option-${k}`}
                    className="flex-1 text-left px-3 py-2 text-sm hover:bg-[#141414] transition-colors"
                    style={{ color: k === value ? "#CCFF00" : undefined }}>
                    {labelFor(k, roleMeta)}
                  </button>
                  <button type="button" aria-label={`About ${labelFor(k, roleMeta)}`}
                    onMouseEnter={() => setHoverKey(k)} onMouseLeave={() => setHoverKey(null)}
                    onClick={() => setTipKey((t) => (t === k ? null : k))}
                    data-testid={`${testId}-info-${k}`}
                    className="px-3 py-2 text-[#525252] hover:text-[#CCFF00] transition-colors">
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </div>
                {showTip && (
                  <div className="px-3 pb-2 text-[11px] leading-relaxed text-[#A3A3A3]" data-testid={`${testId}-tip-${k}`}>
                    {ROLE_TIPS[k]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
